import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  parseBookedTreatmentCourse,
  parseCancelTreatmentCourseResult,
} from '@/lib/bookedTreatmentCourse';
import {
  enrichStaffCourse,
  parseStaffTreatmentCourse,
  staffCourseFromBooked,
  type StaffTreatmentCourse,
} from '@/lib/treatmentCourseStaffView';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { usePatientFormsAssignments } from '@/hooks/useClinioForms';
import { useResources } from '@/hooks/useSchedulingResources';
import { useFormsTemplateList } from '@/hooks/useFormsTemplates';
import type { BookedTreatmentCourse } from '@/types/treatmentCourses';
import type { Json } from '@/types/database.generated';

export function treatmentCoursesForPatientQueryKey(
  clinicId: string | null,
  patientId: string | null,
) {
  return ['treatment-courses', 'patient', clinicId, patientId] as const;
}

export function treatmentCourseQueryKey(clinicId: string | null, courseId: string | null) {
  return ['treatment-courses', 'course', clinicId, courseId] as const;
}

export function treatmentCourseStaffViewQueryKey(
  clinicId: string | null,
  courseId: string | null,
) {
  return ['treatment-courses', 'staff-view', clinicId, courseId] as const;
}

function isMissingStaffViewRpc(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  return (
    error.code === 'PGRST202' ||
    error.code === '42883' ||
    (typeof error.message === 'string' &&
      error.message.toLowerCase().includes('get_treatment_course_staff_view'))
  );
}

export function useTreatmentCoursesForPatient(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) {
  return useQuery({
    queryKey: treatmentCoursesForPatientQueryKey(clinicId ?? null, patientId ?? null),
    enabled: Boolean(clinicId && patientId),
    queryFn: async (): Promise<BookedTreatmentCourse[]> => {
      if (!clinicId || !patientId) return [];
      const { data, error } = await supabase.rpc('list_treatment_courses_for_patient', {
        p_clinic_id: clinicId,
        p_patient_id: patientId,
      });
      if (error) throw error;
      const rows = Array.isArray(data) ? data : [];
      return rows
        .map((row) => parseBookedTreatmentCourse(row))
        .filter((row): row is BookedTreatmentCourse => row !== null);
    },
  });
}

export function useTreatmentCourse(
  clinicId: string | null | undefined,
  courseId: string | null | undefined,
) {
  return useQuery({
    queryKey: treatmentCourseQueryKey(clinicId ?? null, courseId ?? null),
    enabled: Boolean(clinicId && courseId),
    queryFn: async (): Promise<BookedTreatmentCourse | null> => {
      if (!clinicId || !courseId) return null;
      const { data, error } = await supabase.rpc('get_treatment_course', {
        p_clinic_id: clinicId,
        p_course_id: courseId,
      });
      if (error) throw error;
      return parseBookedTreatmentCourse(data);
    },
  });
}

async function loadStaffViewFallback(
  clinicId: string,
  courseId: string,
): Promise<StaffTreatmentCourse | null> {
  const { data, error } = await supabase.rpc('get_treatment_course', {
    p_clinic_id: clinicId,
    p_course_id: courseId,
  });
  if (error) throw error;
  const course = parseBookedTreatmentCourse(data);
  if (!course) return null;
  const raw = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  return staffCourseFromBooked(course, {
    created_at: typeof raw.created_at === 'string' ? raw.created_at : null,
    booked_by: typeof raw.booked_by === 'string' ? raw.booked_by : null,
  });
}

export function useTreatmentCourseStaffView(
  clinicId: string | null | undefined,
  courseId: string | null | undefined,
) {
  const courseQuery = useQuery({
    queryKey: treatmentCourseStaffViewQueryKey(clinicId ?? null, courseId ?? null),
    enabled: Boolean(clinicId && courseId),
    queryFn: async (): Promise<StaffTreatmentCourse | null> => {
      if (!clinicId || !courseId) return null;
      const { data, error } = await supabase.rpc('get_treatment_course_staff_view', {
        p_clinic_id: clinicId,
        p_course_id: courseId,
      });
      if (!error) return parseStaffTreatmentCourse(data);
      if (!isMissingStaffViewRpc(error)) throw error;
      return loadStaffViewFallback(clinicId, courseId);
    },
  });

  const appointmentIds = useMemo(
    () =>
      (courseQuery.data?.steps ?? [])
        .map((step) => step.appointment_id)
        .filter((id): id is string => Boolean(id)),
    [courseQuery.data],
  );

  const extrasQuery = useQuery({
    queryKey: [...treatmentCourseStaffViewQueryKey(clinicId ?? null, courseId ?? null), 'extras', appointmentIds],
    enabled: Boolean(clinicId && courseQuery.data),
    queryFn: async () => {
      if (!clinicId) {
        return { appointments: [], jobs: [], journals: [] };
      }
      const appointmentFilter = appointmentIds.length > 0 ? appointmentIds : ['00000000-0000-0000-0000-000000000000'];
      const [appointmentsRes, jobsRes, journalsRes] = await Promise.all([
        supabase
          .from('appointments')
          .select('id, status, source')
          .eq('clinic_id', clinicId)
          .in('id', appointmentFilter),
        supabase
          .from('appointment_pre_visit_jobs')
          .select('*')
          .eq('clinic_id', clinicId)
          .in('appointment_id', appointmentFilter),
        supabase
          .from('journal_entries')
          .select('id, appointment_id')
          .eq('clinic_id', clinicId)
          .in('appointment_id', appointmentFilter)
          .is('archived_at', null)
          .is('discarded_at', null),
      ]);
      if (appointmentsRes.error) throw appointmentsRes.error;
      if (jobsRes.error) throw jobsRes.error;
      if (journalsRes.error) throw journalsRes.error;
      return {
        appointments: appointmentsRes.data ?? [],
        jobs: (jobsRes.data ?? []) as Array<Record<string, unknown>>,
        journals: journalsRes.data ?? [],
      };
    },
  });

  const { data: members } = useClinicMembers(clinicId);
  const { data: resources } = useResources(clinicId);
  const { data: assignments } = usePatientFormsAssignments(courseQuery.data?.patient_id, clinicId);
  const { data: templateLibrary } = useFormsTemplateList(clinicId);

  const memberNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of members ?? []) {
      map.set(member.user_id, member.name || member.email);
    }
    return map;
  }, [members]);

  const resourceNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const resource of resources ?? []) {
      map.set(resource.id, resource.name);
    }
    return map;
  }, [resources]);

  const templateNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const template of [
      ...(templateLibrary?.platform ?? []),
      ...(templateLibrary?.clinic ?? []),
    ]) {
      map.set(template.id, template.display_name_da || template.display_name_en || template.id);
    }
    return map;
  }, [templateLibrary]);

  const data = useMemo(() => {
    if (!courseQuery.data) return null;
    return enrichStaffCourse(courseQuery.data, {
      appointments: extrasQuery.data?.appointments ?? [],
      jobs: (extrasQuery.data?.jobs ?? []).map((job) => ({
        id: typeof job.id === 'string' ? job.id : undefined,
        appointment_id: typeof job.appointment_id === 'string' ? job.appointment_id : null,
        treatment_course_step_automation_id:
          typeof job.treatment_course_step_automation_id === 'string'
            ? job.treatment_course_step_automation_id
            : null,
        forms_template_id: typeof job.forms_template_id === 'string' ? job.forms_template_id : null,
        status: typeof job.status === 'string' ? job.status : null,
        reason: typeof job.reason === 'string' ? job.reason : null,
        scheduled_for: typeof job.scheduled_for === 'string' ? job.scheduled_for : null,
        attempted_at: typeof job.attempted_at === 'string' ? job.attempted_at : null,
        created_at: typeof job.created_at === 'string' ? job.created_at : null,
      })),
      assignments: (assignments ?? []).map((row) => ({
        id: row.id,
        appointment_id: row.appointment_id,
        forms_assignment_id: row.forms_assignment_id,
        forms_template_id: row.forms_template_id,
        template_summary: row.template_summary,
        status: row.status,
        sent_at: row.sent_at,
        completed_at: row.completed_at,
        score: row.score,
        metadata: row.metadata,
      })),
      journals: extrasQuery.data?.journals ?? [],
      memberNames,
      resourceNames,
      templateNames,
    });
  }, [
    assignments,
    courseQuery.data,
    extrasQuery.data,
    memberNames,
    resourceNames,
    templateNames,
  ]);

  return {
    ...courseQuery,
    data,
    isLoading: courseQuery.isLoading,
    error: courseQuery.error ?? extrasQuery.error,
    refetch: async () => {
      await Promise.all([courseQuery.refetch(), extrasQuery.refetch()]);
    },
  };
}

export function useTreatmentCoursesPatientList(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) {
  const listQuery = useTreatmentCoursesForPatient(clinicId, patientId);
  const appointmentIds = useMemo(
    () =>
      (listQuery.data ?? [])
        .flatMap((course) => course.steps.map((step) => step.appointment_id))
        .filter((id): id is string => Boolean(id)),
    [listQuery.data],
  );

  const extrasQuery = useQuery({
    queryKey: [
      ...treatmentCoursesForPatientQueryKey(clinicId ?? null, patientId ?? null),
      'extras',
      appointmentIds,
    ],
    enabled: Boolean(clinicId && listQuery.data),
    queryFn: async () => {
      if (!clinicId) {
        return { appointments: [], jobs: [], journals: [] };
      }
      const appointmentFilter =
        appointmentIds.length > 0 ? appointmentIds : ['00000000-0000-0000-0000-000000000000'];
      const [appointmentsRes, jobsRes, journalsRes] = await Promise.all([
        supabase
          .from('appointments')
          .select('id, status, source')
          .eq('clinic_id', clinicId)
          .in('id', appointmentFilter),
        supabase
          .from('appointment_pre_visit_jobs')
          .select('*')
          .eq('clinic_id', clinicId)
          .in('appointment_id', appointmentFilter),
        supabase
          .from('journal_entries')
          .select('id, appointment_id')
          .eq('clinic_id', clinicId)
          .in('appointment_id', appointmentFilter)
          .is('archived_at', null)
          .is('discarded_at', null),
      ]);
      if (appointmentsRes.error) throw appointmentsRes.error;
      if (jobsRes.error) throw jobsRes.error;
      if (journalsRes.error) throw journalsRes.error;
      return {
        appointments: appointmentsRes.data ?? [],
        jobs: (jobsRes.data ?? []) as Array<Record<string, unknown>>,
        journals: journalsRes.data ?? [],
      };
    },
  });

  const { data: members } = useClinicMembers(clinicId);
  const { data: resources } = useResources(clinicId);
  const { data: assignments } = usePatientFormsAssignments(patientId, clinicId);
  const { data: templateLibrary } = useFormsTemplateList(clinicId);

  const memberNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of members ?? []) {
      map.set(member.user_id, member.name || member.email);
    }
    return map;
  }, [members]);

  const resourceNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const resource of resources ?? []) {
      map.set(resource.id, resource.name);
    }
    return map;
  }, [resources]);

  const templateNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const template of [
      ...(templateLibrary?.platform ?? []),
      ...(templateLibrary?.clinic ?? []),
    ]) {
      map.set(template.id, template.display_name_da || template.display_name_en || template.id);
    }
    return map;
  }, [templateLibrary]);

  const data = useMemo(() => {
    return (listQuery.data ?? []).map((course) =>
      enrichStaffCourse(staffCourseFromBooked(course), {
        appointments: extrasQuery.data?.appointments ?? [],
        jobs: (extrasQuery.data?.jobs ?? []).map((job) => ({
          id: typeof job.id === 'string' ? job.id : undefined,
          appointment_id: typeof job.appointment_id === 'string' ? job.appointment_id : null,
          treatment_course_step_automation_id:
            typeof job.treatment_course_step_automation_id === 'string'
              ? job.treatment_course_step_automation_id
              : null,
          forms_template_id: typeof job.forms_template_id === 'string' ? job.forms_template_id : null,
          status: typeof job.status === 'string' ? job.status : null,
          reason: typeof job.reason === 'string' ? job.reason : null,
          scheduled_for: typeof job.scheduled_for === 'string' ? job.scheduled_for : null,
          attempted_at: typeof job.attempted_at === 'string' ? job.attempted_at : null,
          created_at: typeof job.created_at === 'string' ? job.created_at : null,
        })),
        assignments: (assignments ?? []).map((row) => ({
          id: row.id,
          appointment_id: row.appointment_id,
          forms_assignment_id: row.forms_assignment_id,
          forms_template_id: row.forms_template_id,
          template_summary: row.template_summary,
          status: row.status,
          sent_at: row.sent_at,
          completed_at: row.completed_at,
          score: row.score,
          metadata: row.metadata,
        })),
        journals: extrasQuery.data?.journals ?? [],
        memberNames,
        resourceNames,
        templateNames,
      }),
    );
  }, [
    assignments,
    extrasQuery.data,
    listQuery.data,
    memberNames,
    resourceNames,
    templateNames,
  ]);

  return {
    ...listQuery,
    data,
    isLoading: listQuery.isLoading,
    error: listQuery.error ?? extrasQuery.error,
    refetch: async () => {
      await Promise.all([listQuery.refetch(), extrasQuery.refetch()]);
    },
  };
}

function applyCourseToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  clinicId: string,
  course: BookedTreatmentCourse,
) {
  queryClient.setQueryData(treatmentCourseQueryKey(clinicId, course.id), course);
  queryClient.invalidateQueries({
    queryKey: treatmentCourseStaffViewQueryKey(clinicId, course.id),
  });
  queryClient.setQueryData(
    treatmentCoursesForPatientQueryKey(clinicId, course.patient_id),
    (current: BookedTreatmentCourse[] | undefined) => {
      if (!current) return [course];
      return current.map((row) => (row.id === course.id ? course : row));
    },
  );
}

export function useSaveTreatmentCourseStep(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { stepId: string; payload: Record<string, unknown> }) => {
      if (!clinicId) throw new Error('clinic required');
      const { data, error } = await supabase.rpc('save_treatment_course_step', {
        p_clinic_id: clinicId,
        p_step_id: input.stepId,
        p_payload: input.payload as unknown as Json,
      });
      if (error) throw error;
      const course = parseBookedTreatmentCourse(data);
      if (!course) throw new Error('invalid course');
      return course;
    },
    onSuccess: (course) => {
      if (clinicId) applyCourseToCache(queryClient, clinicId, course);
    },
  });
}

export function useAddTreatmentCourseStep(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { courseId: string; payload: Record<string, unknown> }) => {
      if (!clinicId) throw new Error('clinic required');
      const { data, error } = await supabase.rpc('add_treatment_course_step', {
        p_clinic_id: clinicId,
        p_course_id: input.courseId,
        p_payload: input.payload as unknown as Json,
      });
      if (error) throw error;
      const course = parseBookedTreatmentCourse(data);
      if (!course) throw new Error('invalid course');
      return course;
    },
    onSuccess: (course) => {
      if (clinicId) applyCourseToCache(queryClient, clinicId, course);
    },
  });
}

export function useCancelTreatmentCourseStep(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { stepId: string; reason?: string | null }) => {
      if (!clinicId) throw new Error('clinic required');
      const { data, error } = await supabase.rpc('cancel_treatment_course_step', {
        p_clinic_id: clinicId,
        p_step_id: input.stepId,
        p_reason: input.reason ?? undefined,
      });
      if (error) throw error;
      const course = parseBookedTreatmentCourse(data);
      if (!course) throw new Error('invalid course');
      return course;
    },
    onSuccess: (course) => {
      if (clinicId) applyCourseToCache(queryClient, clinicId, course);
    },
  });
}

export function useCancelTreatmentCourse(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { courseId: string; reason?: string | null }) => {
      if (!clinicId) throw new Error('clinic required');
      const { data, error } = await supabase.rpc('cancel_treatment_course', {
        p_clinic_id: clinicId,
        p_course_id: input.courseId,
        p_reason: input.reason ?? undefined,
      });
      if (error) throw error;
      const parsed = parseCancelTreatmentCourseResult(data);
      if (!parsed) throw new Error('invalid course');
      return parsed;
    },
    onSuccess: ({ course }) => {
      if (clinicId) applyCourseToCache(queryClient, clinicId, course);
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['treatment-courses'] });
      queryClient.invalidateQueries({ queryKey: ['occupying-resource-allocations'] });
      queryClient.invalidateQueries({ queryKey: ['appointment-resource-allocations'] });
    },
  });
}

export function useRemoveTreatmentCourseStep(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { stepId: string }) => {
      if (!clinicId) throw new Error('clinic required');
      const { data, error } = await supabase.rpc('remove_treatment_course_step', {
        p_clinic_id: clinicId,
        p_step_id: input.stepId,
      });
      if (error) throw error;
      const course = parseBookedTreatmentCourse(data);
      if (!course) throw new Error('invalid course');
      return course;
    },
    onSuccess: (course) => {
      if (clinicId) applyCourseToCache(queryClient, clinicId, course);
    },
  });
}
